import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

const STATUS_MAP = {
  growing:       { cls:'badge--growing', label:' Growing' },
  ready_to_sell: { cls:'badge--ready',   label:'🟢 Ready to Sell' },
  sold:          { cls:'badge--sold',    label:'✅ Sold' },
  closed:        { cls:'badge--closed',  label:'🔒 Closed' },
};

const AVATAR_MAP = {
  farmer_m:'🧑‍🌾', farmer_f:'👩‍🌾', boy:'👨', girl:'👩',
  old_m:'👴', old_f:'👵', young_m:'🧑', young_f:'👧',
  turban:'👳', hat:'🤠', robot:'🤖', alien:'👽',
};

function LazyImage({ src, alt }) {
  const [loaded, setLoaded]   = useState(false);
  const [visible, setVisible] = useState(false);
  const [error, setError]     = useState(false);
  const ref = useRef();

  useEffect(() => {
    if (!src || src.startsWith('data:')) { setError(true); return; }
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) { setVisible(true); observer.disconnect(); } },
      { rootMargin: '100px' }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, [src]);

  if (!src || error) {
    return (
      <div ref={ref} style={{ width:'100%', height:'100%', display:'flex', alignItems:'center', justifyContent:'center', background:'#F0F9F0', fontSize:32 }}></div>
    );
  }

  return (
    <div ref={ref} style={{ width:'100%', height:'100%', position:'relative' }}>
      {!loaded && (
        <div style={{ position:'absolute', inset:0, background:'linear-gradient(90deg,#f0f0f0 25%,#e8e8e8 50%,#f0f0f0 75%)', backgroundSize:'200% 100%', animation:'shimmer 1.5s infinite' }} />
      )}
      {visible && (
        <img src={src} alt={alt} loading="lazy" decoding="async"
          onLoad={() => setLoaded(true)} onError={() => setError(true)}
          style={{ width:'100%', height:'100%', objectFit:'cover', opacity: loaded ? 1 : 0, transition:'opacity 0.3s ease' }}
        />
      )}
      <style>{`@keyframes shimmer{0%{background-position:-200% 0}100%{background-position:200% 0}}`}</style>
    </div>
  );
}

// ── Farmer avatar — shows profile image or emoji or letter ──
function FarmerAvatar({ farmer, size = 28 }) {
  const [imgError, setImgError] = useState(false);

  const hasImg = farmer?.profileImageUrl &&
    !farmer.profileImageUrl.startsWith('data:') &&
    !imgError;

  const emoji  = AVATAR_MAP[farmer?.avatarId];
  const letter = farmer?.name?.[0]?.toUpperCase() || 'F';

  return (
    <div style={{
      width: size, height: size, borderRadius: '50%',
      background: '#1A6B3C20', overflow: 'hidden', flexShrink: 0,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      fontSize: size * 0.5, border: '1.5px solid #1A6B3C30',
    }}>
      {hasImg ? (
        <img src={farmer.profileImageUrl} alt={farmer.name}
          style={{ width:'100%', height:'100%', objectFit:'cover' }}
          onError={() => setImgError(true)} />
      ) : emoji ? (
        <span>{emoji}</span>
      ) : (
        <span style={{ color:'#1A6B3C', fontWeight:700, fontSize: size * 0.45 }}>{letter}</span>
      )}
    </div>
  );
}

export default function CropCard({ crop, showFarmer = true, compact = false }) {
  const navigate = useNavigate();
  const stage    = crop.latestStage || crop.stages?.[crop.stages?.length - 1];
  const thumb    = stage?.mediaUrls?.find(u => u && !u.startsWith('data:'));
  const st       = STATUS_MAP[crop.status] || STATUS_MAP.growing;

  return (
    <div className={`crop-card${compact ? ' crop-card--compact' : ''} anim-fade`}
      onClick={() => navigate(`/crop/${crop._id}`)}>

      <div className="crop-card__thumb">
        <LazyImage src={thumb} alt={crop.cropName} />
        <div className="crop-card__thumb-overlay" />
        <div className="crop-card__status-badge">
          <span className={`badge ${st.cls}`}>{st.label}</span>
        </div>
        <div className="crop-card__day-badge">Day {crop.currentDayCount || 0}</div>
      </div>

      <div className="crop-card__body">
        <div className="crop-card__title-row">
          <div>
            <div className="crop-card__name">{crop.cropName}</div>
            <div className="crop-card__category">
              {crop.category}{crop.subCategory ? ` · ${crop.subCategory}` : ''}
            </div>
          </div>
          {crop.price && (
            <div className="crop-card__price">
              <div className="crop-card__price-value">₹{crop.price}</div>
              <div className="crop-card__price-unit">{crop.priceUnit?.replace(/_/g,' ')}</div>
            </div>
          )}
        </div>

        {/* ── Farmer row with real profile image ── */}
        {showFarmer && crop.userId && (
          <div className="crop-card__farmer-row">
            <FarmerAvatar farmer={crop.userId} size={26} />
            <span className="crop-card__farmer-name">{crop.userId.name || 'Farmer'}</span>
            {crop.userId.locationAddress && (
              <span className="crop-card__location">
                · {crop.userId.locationAddress.split(',')[0]}
              </span>
            )}
          </div>
        )}

        {stage?.description && <p className="crop-card__desc">{stage.description}</p>}

        <div className="crop-card__footer">
          <span>❤️ {crop.likeCount || 0}</span>
          <span>💬 {crop.commentCount || 0}</span>
          <span>⭐ {crop.bestCropCount || 0}</span>
          {crop.stages && (
            <span className="crop-card__footer-right">
              {crop.stages.length} stage{crop.stages.length !== 1 ? 's' : ''}
            </span>
          )}
        </div>
      </div>
    </div>
  );
}